/**
 * USE-PREVENTION-LEVEL-ASSISTANT
 * Propose un niveau de prevention pour un risque a partir des seules mesures reellement en place.
 * Aucune mesure en place : c'est le niveau le plus bas de l'echelle, sans deranger l'assistant.
 * La valeur est rendue a l'appelant, jamais enregistree : elle doit toujours passer sous les yeux
 * de l'utilisateur, sur l'etape Evaluation.
 */
import { useState } from "react"
import { toast } from "react-toastify"
import { askAssistantJson } from "@/utils/assistant.utils"
import { PreventionAction } from "@/entities/prevention-action/model/prevention-action.types"
import { PhysicalRisk, PhysicalRiskScaleItem } from "../model/physical-risk.types"
import { describePreventionActions, describeRisk } from "../model/physical-risk.prompt"

const PREPROMPT = 'Retourner uniquement un JSON valide au format { "prevention": 1 }.'
const ERROR_MESSAGE = "L'assistant n'a pas pu proposer de niveau de prévention."

//Une mesure en place est une mesure realisee : une mesure prevue ne protege encore personne
function getInPlacePreventionActions(actions: PreventionAction[]) {
  return actions.filter((action) => action.cStatus === "done")
}

//Signature du contenu des mesures en place : inchangee, il n'y a rien a reevaluer
export function getInPlacePreventionSignature(actions: PreventionAction[]) {
  return getInPlacePreventionActions(actions)
    .map((action) => [action.id, action.name, action.description, action.refreshDelay].join("|"))
    .sort()
    .join("~")
}

//Ids des mesures en place : ils disent si l'ensemble s'est seulement enrichi depuis la derniere fois
export function getInPlacePreventionIds(actions: PreventionAction[]) {
  return getInPlacePreventionActions(actions).map((action) => action.id)
}

//Aucune mesure retiree : cocher ou ajouter une mesure ne peut pas affaiblir le niveau de prevention
export function isPreventionOnlyReinforced(previousInPlaceIds: string[], actions: PreventionAction[]) {
  const inPlaceIds = new Set(getInPlacePreventionIds(actions))
  return previousInPlaceIds.every((id) => inPlaceIds.has(id))
}

function getLowestValue(scale: PhysicalRiskScaleItem[]) {
  return Math.min(...scale.map((item) => item.value))
}

function getPrompt(risk: PhysicalRisk, actions: PreventionAction[], scale: PhysicalRiskScaleItem[]) {
  //Le niveau de prevention actuel est justement ce que l'on fait evaluer : il reste hors du prompt
  return `Tu es un expert en prévention des risques professionnels.

Évalue le niveau de maîtrise du risque suivant, au vu des seules mesures de prévention déjà en place.

${describeRisk(risk, { isPreventionHidden: true })}

${describePreventionActions("Mesures en place", actions)}

Échelle de prévention à utiliser, n'invente aucune autre valeur :
${scale.map((item) => `- ${item.value} : ${item.label}${item.description ? ` — ${item.description}` : ""}`).join("\n")}

Retourne uniquement un JSON valide au format :
{ "prevention": <une des valeurs de l'échelle> }`
}

function parsePreventionValue(parsed: any, scale: PhysicalRiskScaleItem[]) {
  const value = Number(parsed?.prevention ?? parsed?.value ?? parsed)
  if (!Number.isFinite(value)) return null

  //L'assistant peut sortir de l'echelle : on retient la valeur autorisee la plus proche
  return scale.reduce(
    (closest, item) => (Math.abs(item.value - value) < Math.abs(closest - value) ? item.value : closest),
    scale[0].value
  )
}

//Le niveau deja retenu fait plancher quand les mesures en place se sont seulement enrichies
function applyFloor(value: number | null, floor?: number) {
  if (value === null || floor === undefined) return value
  return Math.max(value, floor)
}

export function usePreventionLevelAssistant() {
  const [isSuggestingPrevention, setIsSuggestingPrevention] = useState(false)

  //Rend le niveau propose, ou null si l'assistant n'a rien pu dire : l'appelant garde alors sa valeur.
  //floor : l'assistant reevalue tout depuis zero, il pourrait donc proposer moins bien qu'avant alors
  //qu'une protection vient d'etre mise en place. L'appelant le passe quand rien n'a ete retire.
  function suggestPreventionLevel(
    risk: PhysicalRisk,
    actions: PreventionAction[],
    floor?: number
  ): Promise<number | null> {
    const scale = risk.cPreventionScale
    if (!scale?.length) return Promise.resolve(null)

    const inPlaceActions = getInPlacePreventionActions(actions)
    if (!inPlaceActions.length) return Promise.resolve(getLowestValue(scale))

    setIsSuggestingPrevention(true)

    return new Promise((resolve) => {
      askAssistantJson({
        preprompt: PREPROMPT,
        prompt: getPrompt(risk, inPlaceActions, scale),
        onDone: (response) => {
          setIsSuggestingPrevention(false)
          resolve(applyFloor(parsePreventionValue(response, scale), floor))
        },
        onError: () => {
          setIsSuggestingPrevention(false)
          toast(ERROR_MESSAGE, { type: "error" })
          resolve(null)
        }
      })
    })
  }

  return { isSuggestingPrevention, suggestPreventionLevel }
}
